import { ChevronDown } from "lucide-react"
import { useState } from "react"

import { monto as comoCifra } from "@/componentes/tesoreria/dinero"
import { indicadores, type Partida } from "./indicadoresCartera"
import { type FilaSaldo, TablaSaldos } from "./TablaSaldos"

export type GrupoCartera = {
  id: number
  nombre: string
  /** Las filas del grupo, sin el nombre del tercero delante: ya lo dice la tarjeta. */
  filas: FilaSaldo[]
  partidas: Partida[]
}

/**
 * La cartera agrupada por cliente —o por proveedor—: una tarjeta por cada uno con su
 * saldo, y dentro sus expedientes (16/10, pedido del usuario: «en el 2.0 tengo
 * totalizadas las CxC por cliente»).
 *
 * ⚠ **Plegadas al abrir**, como el agrupado del 2.0: se abre el cliente que interesa.
 * Y la cabecera es un `button` con `aria-expanded`, no un `div` con `onClick`.
 *
 * ⚠ **El saldo de la tarjeta va por moneda y nunca sumado**, como todo indicador.
 * Ordenadas de la que más debe a la que menos (por dólares, que es la moneda de la
 * cartera), y a igualdad por nombre.
 */
export function CarteraPorTercero({
  grupos,
  hoy,
  etiquetaAplicado,
  accion,
  onAccion,
  accionSecundaria,
  onAccionSecundaria,
  onAbrir,
}: {
  grupos: GrupoCartera[]
  hoy: string
  etiquetaAplicado: string
  accion?: string
  onAccion?: (id: number) => void
  accionSecundaria?: string
  onAccionSecundaria?: (id: number) => void
  onAbrir?: (id: number) => void
}) {
  const [abiertos, setAbiertos] = useState<Set<number>>(new Set())

  if (grupos.length === 0) {
    return (
      <p className="superficie border-dashed p-6 text-center" style={{ color: "var(--fg-muted)" }}>
        No hay nada aquí todavía.
      </p>
    )
  }

  const conIndicadores = grupos
    .map((g) => ({ ...g, kpi: indicadores(g.partidas, hoy) }))
    .sort((a, b) => {
      const usd = (x: typeof a) =>
        Number(x.kpi.abiertas.montos.find((m) => m.divisa === "USD")?.monto ?? 0)
      return usd(b) - usd(a) || a.nombre.localeCompare(b.nombre, "es")
    })

  function alternar(id: number) {
    setAbiertos((actual) => {
      const nuevo = new Set(actual)
      if (nuevo.has(id)) nuevo.delete(id)
      else nuevo.add(id)
      return nuevo
    })
  }

  return (
    <ul className="space-y-3">
      {conIndicadores.map((g) => {
        const abierto = abiertos.has(g.id)
        const idPanel = `cartera-grupo-${g.id}`
        const { abiertas, vencidas } = g.kpi
        return (
          <li key={g.id} className="tarjeta overflow-hidden p-0" data-testid={`grupo-${g.id}`}>
            <button
              type="button"
              aria-expanded={abierto}
              aria-controls={idPanel}
              onClick={() => alternar(g.id)}
              className="foco-cristal toque-cristal flex w-full flex-wrap items-center gap-3 p-4 text-left"
            >
              <ChevronDown
                size={16}
                aria-hidden={true}
                className="shrink-0 transition-transform motion-reduce:transition-none"
                style={{ transform: abierto ? "rotate(180deg)" : undefined }}
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold">{g.nombre}</span>
                <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                  {g.filas.length} {g.filas.length === 1 ? "cuenta" : "cuentas"}
                  {abiertas.cuentas > 0 ? ` · ${abiertas.cuentas} con saldo` : " · al día"}
                  {vencidas.cuentas > 0 && (
                    <span style={{ color: "var(--destructive)" }}>
                      {` · ${vencidas.cuentas} ${vencidas.cuentas === 1 ? "vencida" : "vencidas"}`}
                    </span>
                  )}
                </span>
              </span>
              <span className="text-right text-sm font-semibold tabular-nums">
                {abiertas.montos.length === 0 ? (
                  <span style={{ color: "var(--fg-muted)" }}>Sin saldo</span>
                ) : (
                  abiertas.montos.map((m) => (
                    <span key={m.divisa} className="block">
                      {comoCifra(m.monto)} {m.divisa}
                    </span>
                  ))
                )}
              </span>
            </button>
            {abierto && (
              <div
                id={idPanel}
                className="border-t p-2 sm:p-3"
                style={{ borderColor: "var(--border)" }}
              >
                <TablaSaldos
                  filas={g.filas}
                  etiquetaAplicado={etiquetaAplicado}
                  accion={accion}
                  onAccion={onAccion}
                  accionSecundaria={accionSecundaria}
                  onAccionSecundaria={onAccionSecundaria}
                  onAbrir={onAbrir}
                />
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
